import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { User, Users, FileText, CheckCircle, Search, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { alunosService } from '../services/alunosService';
import { contratosService, type CreateContratoDto } from '../services/contratosService';
import { responsaveisService, type Responsavel, type CreateResponsavelDto } from '../services/responsaveisService';
import turmasService, { type TurmaResponse } from '../services/turmasService';

// â”€â”€â”€ Styled Components â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 1rem;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 0.75rem;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 640px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  display: flex;
  border-radius: 0.25rem;
  &:hover { color: #111827; background: #f3f4f6; }
`;

// â”€â”€â”€ Stepper â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const StepperRow = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem 0;
  gap: 0;
`;

const StepWrap = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const StepCircle = styled.div<{ $active: boolean; $done: boolean }>`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  flex-shrink: 0;
  background: ${p => p.$done ? '#2563eb' : p.$active ? '#2563eb' : '#e5e7eb'};
  color: ${p => (p.$done || p.$active) ? '#fff' : '#9ca3af'};
  border: 2px solid ${p => (p.$done || p.$active) ? '#2563eb' : '#e5e7eb'};
`;

const StepLabel = styled.span<{ $active: boolean }>`
  font-size: 0.7rem;
  color: ${p => p.$active ? '#2563eb' : '#9ca3af'};
  font-weight: ${p => p.$active ? '600' : '400'};
  white-space: nowrap;
  margin-left: 0.4rem;
`;

const StepLine = styled.div<{ $done: boolean }>`
  flex: 1;
  height: 2px;
  background: ${p => p.$done ? '#2563eb' : '#e5e7eb'};
  margin: 0 0.5rem;
`;

// â”€â”€â”€ Body â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 1rem;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const FullRow = styled.div`
  grid-column: 1 / -1;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const Label = styled.label`
  font-size: 0.8125rem;
  font-weight: 500;
  color: #374151;
  span { color: #dc2626; }
`;

const Input = styled.input<{ $error?: boolean }>`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${p => p.$error ? '#dc2626' : '#d1d5db'};
  border-radius: 0.375rem;
  font-size: 0.875rem;
  outline: none;
  &:focus { border-color: #2563eb; box-shadow: 0 0 0 2px rgba(37,99,235,0.15); }
`;

const Select = styled.select<{ $error?: boolean }>`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${p => p.$error ? '#dc2626' : '#d1d5db'};
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: #fff;
  outline: none;
  &:focus { border-color: #2563eb; box-shadow: 0 0 0 2px rgba(37,99,235,0.15); }
`;

const ErrorMsg = styled.span`
  font-size: 0.75rem;
  color: #dc2626;
`;

// â”€â”€â”€ ResponsÃ¡veis â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  &:focus-within { border-color: #2563eb; }
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  font-size: 0.875rem;
`;

const ResponsavelList = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  max-height: 180px;
  overflow-y: auto;
  margin-top: 0.5rem;
`;

const ResponsavelItem = styled.div<{ $selected: boolean }>`
  padding: 0.6rem 0.75rem;
  cursor: pointer;
  background: ${p => p.$selected ? '#eff6ff' : '#fff'};
  border-left: 3px solid ${p => p.$selected ? '#2563eb' : 'transparent'};
  border-bottom: 1px solid #f3f4f6;
  &:hover { background: ${p => p.$selected ? '#eff6ff' : '#f9fafb'}; }
  &:last-child { border-bottom: none; }
`;

const ResponsavelNome = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
`;

const ResponsavelCpf = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

const SelectedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1d4ed8;
  font-weight: 500;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  padding: 0.1rem;
  border-radius: 0.25rem;
  margin-left: auto;
  &:hover { color: #dc2626; }
`;

const InlineActions = styled.div`
  margin-top: 0.5rem;
  display: flex;
  justify-content: flex-end;
`;

const LinkActionBtn = styled.button`
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  &:hover { color: #1d4ed8; text-decoration: underline; }
`;

const InlineCreateCard = styled.div`
  margin-top: 1rem;
  border: 1px solid #dbeafe;
  background: #eff6ff;
  border-radius: 0.5rem;
  padding: 0.875rem;
`;

const InlineCreateTitle = styled.h4`
  margin: 0 0 0.75rem;
  color: #1e3a8a;
  font-size: 0.8125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const InlineCreateGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const InlineCreateActions = styled.div`
  margin-top: 0.75rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const BtnGhost = styled.button`
  padding: 0.45rem 0.9rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  background: #fff;
  color: #334155;
  font-size: 0.8125rem;
  cursor: pointer;
  &:hover { background: #f8fafc; }
`;

const BtnPrimarySmall = styled.button`
  padding: 0.45rem 0.9rem;
  border: none;
  border-radius: 0.375rem;
  background: #2563eb;
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #1d4ed8; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

// â”€â”€â”€ Contrato â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
`;

const Divider = styled.div`
  border-top: 1px dashed #e5e7eb;
  margin: 1rem 0 0.75rem;
`;

const DividerLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  background: #fff;
  padding: 0 0.5rem;
  position: relative;
  top: -0.6rem;
`;

const ReadOnlyValue = styled.div`
  padding: 0.5rem 0.75rem;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #374151;
  min-height: 2.375rem;
  display: flex;
  align-items: center;
`;

const Grid3 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.75rem;
`;

// â”€â”€â”€ RevisÃ£o â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const ReviewSection = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 1rem;
`;

const ReviewHeader = styled.div`
  background: #f9fafb;
  padding: 0.5rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
`;

const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
`;

const ReviewItem = styled.div`
  padding: 0.5rem 0.75rem;
  border-top: 1px solid #f3f4f6;
  &:nth-child(odd) { border-right: 1px solid #f3f4f6; }
`;

const ReviewLabel = styled.div`
  font-size: 0.7rem;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ReviewValue = styled.div`
  font-size: 0.875rem;
  color: #111827;
  font-weight: 500;
  margin-top: 0.1rem;
`;

// â”€â”€â”€ Footer â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const Footer = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const NavGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const BtnCancel = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: #fff;
  color: #374151;
  font-size: 0.875rem;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
`;

const BtnBack = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: #fff;
  color: #374151;
  font-size: 0.875rem;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
`;

const BtnNext = styled.button`
  padding: 0.5rem 1.25rem;
  border: none;
  border-radius: 0.375rem;
  background: #2563eb;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  &:hover { background: #1d4ed8; }
`;

const BtnSubmit = styled.button`
  padding: 0.5rem 1.25rem;
  border: none;
  border-radius: 0.375rem;
  background: #16a34a;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  &:hover { background: #15803d; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

// â”€â”€â”€ Tipos â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface WizardData {
  nome: string;
  matricula: string;
  dataNascimento: string;
  turmaId: number;
  cpf: string;
  rg: string;
  email: string;
  telefone: string;
  endereco: string;
  responsavelPrincipalId: number | null;
  responsavelSolidarioId: number | null;
  eRematricula: boolean;
  valorMensal: string;
  percentualDesconto: string;
  diaVencimento: string;
  valorMatricula: string;
  percentualDescontoMatricula: string;
  formaPagamentoMatricula: 'avista' | 'parcelado' | 'isento';
  parcelasMatricula: string;
  valorMaterial: string;
  percentualDescontoMaterial: string;
  formaPagamentoMaterial: 'avista' | 'parcelado' | 'isento';
  parcelasMaterial: string;
  emitirNfse: boolean;
}

interface NovoResponsavelForm {
  nome: string;
  cpfCnpj: string;
  email: string;
  telefone: string;
  tipoPessoa: '1' | '2' | '3';
  endereco: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
}

const initialNovoResponsavel: NovoResponsavelForm = {
  nome: '',
  cpfCnpj: '',
  email: '',
  telefone: '',
  tipoPessoa: '1',
  endereco: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: '',
};

const initialData: WizardData = {
  nome: '',
  matricula: '',
  dataNascimento: '',
  turmaId: 0,
  cpf: '',
  rg: '',
  email: '',
  telefone: '',
  endereco: '',
  responsavelPrincipalId: null,
  responsavelSolidarioId: null,
  eRematricula: false,
  valorMensal: '',
  percentualDesconto: '0',
  diaVencimento: '10',
  valorMatricula: '0',
  percentualDescontoMatricula: '0',
  formaPagamentoMatricula: 'isento',
  parcelasMatricula: '1',
  valorMaterial: '0',
  percentualDescontoMaterial: '0',
  formaPagamentoMaterial: 'isento',
  parcelasMaterial: '1',
  emitirNfse: true,
};

interface Props {
  isOpen: boolean;
  anoLetivoId: number;
  onClose: () => void;
  onSuccess: () => void;
}

// â”€â”€â”€ Wizard â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const STEPS = [
  { label: 'Aluno', icon: User },
  { label: 'Responsáveis', icon: Users },
  { label: 'Contrato', icon: FileText },
  { label: 'Revisão', icon: CheckCircle },
];

const fmt = (v: string | number) =>
  `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

const fmtPag = (v: string) =>
  ({ avista: 'À vista', parcelado: 'Parcelado', isento: 'Isento' }[v] ?? v);

const calcWithDiscount = (base: number, discountPercent: number): number =>
  base * (1 - discountPercent / 100);

export const NovoAlunoWizard: React.FC<Props> = ({ isOpen, anoLetivoId, onClose, onSuccess }) => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<WizardData>(initialData);
  const [turmas, setTurmas] = useState<TurmaResponse[]>([]);
  const [responsaveis, setResponsaveis] = useState<Responsavel[]>([]);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof WizardData, string>>>({});
  const [creatingResponsavelFor, setCreatingResponsavelFor] = useState<'principal' | 'solidario' | null>(null);
  const [novoResponsavel, setNovoResponsavel] = useState<NovoResponsavelForm>(initialNovoResponsavel);
  const [novoResponsavelErrors, setNovoResponsavelErrors] = useState<Partial<Record<keyof NovoResponsavelForm, string>>>({});
  const [savingResponsavel, setSavingResponsavel] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setStep(0);
    setData(initialData);
    setErrors({});
    setSearch('');
    setCreatingResponsavelFor(null);
    setNovoResponsavel(initialNovoResponsavel);
    setNovoResponsavelErrors({});
    loadInitialData();
  }, [isOpen]);

  const loadInitialData = async () => {
    try {
      const [turmasData, respData] = await Promise.all([
        turmasService.findAll(),
        responsaveisService.getAtivos(),
      ]);
      setTurmas(turmasData);
      setResponsaveis(respData);
      if (turmasData.length > 0) {
        setData(prev => ({
          ...prev,
          turmaId: turmasData[0].id,
          valorMensal: String(turmasData[0].valorMensalidade),
          valorMatricula: String(turmasData[0].valorMensalidade),
          valorMaterial: String(turmasData[0].valorMaterialDidatico ?? 0),
        }));
      }
    } catch {
      toast.error('Erro ao carregar dados');
    }
  };

  const set = (field: keyof WizardData, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const openCreateResponsavel = (tipo: 'principal' | 'solidario') => {
    setCreatingResponsavelFor(tipo);
    setNovoResponsavel(initialNovoResponsavel);
    setNovoResponsavelErrors({});
  };

  const closeCreateResponsavel = () => {
    setCreatingResponsavelFor(null);
    setNovoResponsavel(initialNovoResponsavel);
    setNovoResponsavelErrors({});
  };

  const handleCreateResponsavel = async () => {
    const formErrors: Partial<Record<keyof NovoResponsavelForm, string>> = {};

    if (!novoResponsavel.nome.trim()) formErrors.nome = 'Obrigatório';
    if (!novoResponsavel.cpfCnpj.trim()) formErrors.cpfCnpj = 'Obrigatório';
    if (!novoResponsavel.email.trim()) formErrors.email = 'Obrigatório';
    if (!novoResponsavel.telefone.trim()) formErrors.telefone = 'Obrigatório';
    if (!novoResponsavel.endereco.trim()) formErrors.endereco = 'Obrigatório';
    if (!novoResponsavel.cidade.trim()) formErrors.cidade = 'Obrigatório';
    if (!novoResponsavel.estado.trim()) formErrors.estado = 'Obrigatório';
    if (!novoResponsavel.cep.trim()) formErrors.cep = 'Obrigatório';

    if (Object.keys(formErrors).length > 0) {
      setNovoResponsavelErrors(formErrors);
      return;
    }

    try {
      setSavingResponsavel(true);
      const payload: CreateResponsavelDto = {
        nome: novoResponsavel.nome.trim(),
        cpfCnpj: novoResponsavel.cpfCnpj.replace(/\D/g, ''),
        email: novoResponsavel.email.trim(),
        telefone: novoResponsavel.telefone.replace(/\D/g, ''),
        tipoPessoa: Number(novoResponsavel.tipoPessoa),
        endereco: {
          endereco: novoResponsavel.endereco.trim(),
          numero: novoResponsavel.numero.trim() || undefined,
          complemento: novoResponsavel.complemento.trim() || undefined,
          bairro: novoResponsavel.bairro.trim() || undefined,
          cidade: novoResponsavel.cidade.trim(),
          estado: novoResponsavel.estado.trim().toUpperCase().slice(0, 2),
          cep: novoResponsavel.cep.replace(/\D/g, ''),
        },
        ativo: true,
      };

      const criado = await responsaveisService.create(payload);
      setResponsaveis(prev => [criado, ...prev]);

      if (creatingResponsavelFor === 'principal') {
        set('responsavelPrincipalId', criado.id);
      } else if (creatingResponsavelFor === 'solidario') {
        set('responsavelSolidarioId', criado.id);
      }

      setSearch('');
      closeCreateResponsavel();
      toast.success('Responsável criado com sucesso');
    } catch (err: any) {
      const msg = err.response?.data?.message;
      toast.error(Array.isArray(msg) ? msg[0] : msg || 'Erro ao criar responsável');
    } finally {
      setSavingResponsavel(false);
    }
  };

  const validate = (): boolean => {
    const e: Partial<Record<keyof WizardData, string>> = {};

    if (step === 0) {
      if (!data.nome.trim()) e.nome = 'Obrigatório';
      if (!data.matricula.trim()) e.matricula = 'Obrigatório';
      if (!data.dataNascimento) e.dataNascimento = 'Obrigatório';
      if (!data.turmaId) e.turmaId = 'Obrigatório';
    }

    if (step === 1) {
      if (!data.responsavelPrincipalId) e.responsavelPrincipalId = 'Selecione o responsável principal';
    }


    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate()) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const aluno = await alunosService.create({
        nome: data.nome.trim(),
        matricula: data.matricula.trim(),
        dataNascimento: data.dataNascimento,
        turmaId: data.turmaId,
        responsavelFinanceiroId: data.responsavelPrincipalId!,
        cpf: data.cpf || undefined,
        rg: data.rg || undefined,
        email: data.email || undefined,
        telefone: data.telefone || undefined,
        endereco: data.endereco || undefined,
      });

      const contrato: CreateContratoDto = {
        alunoId: aluno.id,
        turmaId: data.turmaId,
        anoLetivoId,
        responsavelPrincipalId: data.responsavelPrincipalId!,
        responsavelAlternativoId: data.responsavelSolidarioId ?? undefined,
        eRematricula: data.eRematricula,
        valorMensal: parseFloat(data.valorMensal) || 0,
        percentualDescontoMensal: parseFloat(data.percentualDesconto) || 0,
        diaVencimento: parseInt(data.diaVencimento) || 10,
        valorMatricula: calcWithDiscount(
          parseFloat(data.valorMatricula) || 0,
          parseFloat(data.percentualDescontoMatricula) || 0,
        ),
        formaPagamentoMatricula: data.formaPagamentoMatricula,
        parcelasMatricula: data.formaPagamentoMatricula === 'parcelado' ? parseInt(data.parcelasMatricula) || 1 : data.formaPagamentoMatricula === 'avista' ? 1 : 0,
        valorMaterial: calcWithDiscount(
          parseFloat(data.valorMaterial) || 0,
          parseFloat(data.percentualDescontoMaterial) || 0,
        ),
        formaPagamentoMaterial: data.formaPagamentoMaterial,
        parcelasMaterial: data.formaPagamentoMaterial === 'parcelado' ? parseInt(data.parcelasMaterial) || 1 : data.formaPagamentoMaterial === 'avista' ? 1 : 0,
        emitirNfse: data.emitirNfse,
      };

      await contratosService.create(contrato);
      toast.success(`${aluno.nome} matriculado com sucesso!`);
      onSuccess();
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.message;
      toast.error(Array.isArray(msg) ? msg[0] : msg || 'Erro ao matricular aluno');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredResp = responsaveis.filter(r => {
    if (!search) return true;
    const t = search.toLowerCase();
    return r.nome.toLowerCase().includes(t) || (r.cpfCnpj || '').replace(/\D/g, '').includes(t.replace(/\D/g, ''));
  });

  const principal = responsaveis.find(r => r.id === data.responsavelPrincipalId);
  const solidario = responsaveis.find(r => r.id === data.responsavelSolidarioId);
  const turma = turmas.find(t => t.id === data.turmaId);
  const valorMensalBase = parseFloat(data.valorMensal) || 0;
  const percentualDescontoMensal = parseFloat(data.percentualDesconto) || 0;
  const valorMensalFinal = calcWithDiscount(valorMensalBase, percentualDescontoMensal);
  const valorMatriculaBase = parseFloat(data.valorMatricula) || 0;
  const percentualDescontoMatricula = parseFloat(data.percentualDescontoMatricula) || 0;
  const valorMatriculaFinal = calcWithDiscount(valorMatriculaBase, percentualDescontoMatricula);
  const valorMaterialBase = parseFloat(data.valorMaterial) || 0;
  const percentualDescontoMaterial = parseFloat(data.percentualDescontoMaterial) || 0;
  const valorMaterialFinal = calcWithDiscount(valorMaterialBase, percentualDescontoMaterial);

  if (!isOpen) return null;

  return (
    <Overlay onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal>
        {/* Header */}
        <Header>
          <Title>Nova Matrícula</Title>
          <CloseBtn onClick={onClose}><X size={18} /></CloseBtn>
        </Header>

        {/* Stepper */}
        <StepperRow>
          {STEPS.map((s, i) => (
            <StepWrap key={i}>
              <StepCircle $active={i === step} $done={i < step}>
                {i < step ? '✓' : i + 1}
              </StepCircle>
              <StepLabel $active={i === step}>{s.label}</StepLabel>
              {i < STEPS.length - 1 && <StepLine $done={i < step} />}
            </StepWrap>
          ))}
        </StepperRow>

        {/* Body */}
        <Body>
          {/* â”€â”€ Step 1: Dados do Aluno â”€â”€ */}
          {step === 0 && (
            <>
              <SectionTitle>Dados do Aluno</SectionTitle>
              <Grid2>
                <FullRow>
                  <FormGroup>
                    <Label>Nome completo <span>*</span></Label>
                    <Input value={data.nome} onChange={e => set('nome', e.target.value)} $error={!!errors.nome} placeholder="Nome do aluno" />
                    {errors.nome && <ErrorMsg>{errors.nome}</ErrorMsg>}
                  </FormGroup>
                </FullRow>

                <FormGroup>
                  <Label>Matrícula <span>*</span></Label>
                  <Input value={data.matricula} onChange={e => set('matricula', e.target.value)} $error={!!errors.matricula} placeholder="Ex: 2026001" />
                  {errors.matricula && <ErrorMsg>{errors.matricula}</ErrorMsg>}
                </FormGroup>

                <FormGroup>
                  <Label>Data de nascimento <span>*</span></Label>
                  <Input type="date" value={data.dataNascimento} onChange={e => set('dataNascimento', e.target.value)} $error={!!errors.dataNascimento} />
                  {errors.dataNascimento && <ErrorMsg>{errors.dataNascimento}</ErrorMsg>}
                </FormGroup>

                <FullRow>
                  <FormGroup>
                    <Label>Turma <span>*</span></Label>
                    <Select
                      value={data.turmaId}
                      onChange={e => {
                        const turmaId = Number(e.target.value);
                        const selected = turmas.find(t => t.id === turmaId);
                        setData(prev => ({
                          ...prev,
                          turmaId,
                          valorMensal: selected ? String(selected.valorMensalidade) : prev.valorMensal,
                          valorMatricula: selected ? String(selected.valorMensalidade) : prev.valorMatricula,
                          valorMaterial: selected ? String(selected.valorMaterialDidatico ?? 0) : prev.valorMaterial,
                        }));
                        setErrors(prev => ({ ...prev, turmaId: undefined, valorMensal: undefined }));
                      }}
                      $error={!!errors.turmaId}
                    >
                      <option value={0}>Selecione...</option>
                      {turmas.map(t => (
                        <option key={t.id} value={t.id}>{t.nome}</option>
                      ))}
                    </Select>
                    {errors.turmaId && <ErrorMsg>{errors.turmaId}</ErrorMsg>}
                  </FormGroup>
                </FullRow>

                <FormGroup>
                  <Label>CPF</Label>
                  <Input value={data.cpf} onChange={e => set('cpf', e.target.value)} placeholder="000.000.000-00" />
                </FormGroup>

                <FormGroup>
                  <Label>RG</Label>
                  <Input value={data.rg} onChange={e => set('rg', e.target.value)} placeholder="0000000-0" />
                </FormGroup>

                <FormGroup>
                  <Label>Telefone</Label>
                  <Input value={data.telefone} onChange={e => set('telefone', e.target.value)} placeholder="(00) 00000-0000" />
                </FormGroup>

                <FormGroup>
                  <Label>E-mail</Label>
                  <Input type="email" value={data.email} onChange={e => set('email', e.target.value)} placeholder="email@exemplo.com" />
                </FormGroup>

                <FullRow>
                  <FormGroup>
                    <Label>Endereço</Label>
                    <Input value={data.endereco} onChange={e => set('endereco', e.target.value)} placeholder="Rua, número, bairro, cidade" />
                  </FormGroup>
                </FullRow>
              </Grid2>
            </>
          )}

          {/* â”€â”€ Step 2: ResponsÃ¡veis â”€â”€ */}
          {step === 1 && (
            <>
              <SectionTitle>Responsável Principal (Financeiro) *</SectionTitle>
              {data.responsavelPrincipalId ? (
                <SelectedBadge>
                  <User size={16} />
                  {principal?.nome}
                  <span style={{ color: '#6b7280', fontWeight: 400, fontSize: '0.8rem' }}>
                    &nbsp;— {principal?.cpfCnpj}
                  </span>
                  <RemoveBtn onClick={() => set('responsavelPrincipalId', null)} title="Remover">
                    <X size={14} />
                  </RemoveBtn>
                </SelectedBadge>
              ) : (
                <>
                  <SearchBox>
                    <Search size={16} color="#9ca3af" />
                    <SearchInput
                      ref={searchRef}
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Buscar por nome ou CPF..."
                    />
                  </SearchBox>
                  {errors.responsavelPrincipalId && (
                    <ErrorMsg style={{ display: 'block', marginTop: '0.25rem' }}>
                      {errors.responsavelPrincipalId}
                    </ErrorMsg>
                  )}
                  <ResponsavelList>
                    {filteredResp.length === 0 ? (
                      <div style={{ padding: '1rem', color: '#9ca3af', fontSize: '0.875rem', textAlign: 'center' }}>
                        Nenhum responsável encontrado
                      </div>
                    ) : filteredResp.map(r => (
                      <ResponsavelItem
                        key={r.id}
                        $selected={false}
                        onClick={() => { set('responsavelPrincipalId', r.id); setSearch(''); }}
                      >
                        <ResponsavelNome>{r.nome}</ResponsavelNome>
                        <ResponsavelCpf>{r.cpfCnpj}{r.telefone ? ` · ${r.telefone}` : ''}</ResponsavelCpf>
                      </ResponsavelItem>
                    ))}
                    </ResponsavelList>
                  </>
                )}
              <InlineActions>
                <LinkActionBtn type="button" onClick={() => openCreateResponsavel('principal')}>
                  + Criar novo responsável principal
                </LinkActionBtn>
              </InlineActions>

              <div style={{ marginTop: '1.5rem' }}>
                <SectionTitle>Responsável Solidário (opcional)</SectionTitle>
                {data.responsavelSolidarioId ? (
                  <SelectedBadge>
                    <User size={16} />
                    {solidario?.nome}
                    <span style={{ color: '#6b7280', fontWeight: 400, fontSize: '0.8rem' }}>
                      &nbsp;— {solidario?.cpfCnpj}
                    </span>
                    <RemoveBtn onClick={() => set('responsavelSolidarioId', null)} title="Remover">
                      <X size={14} />
                    </RemoveBtn>
                  </SelectedBadge>
                ) : (
                  <>
                    <SearchBox>
                      <Search size={16} color="#9ca3af" />
                      <SearchInput
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Buscar responsável solidário..."
                      />
                    </SearchBox>
                    <ResponsavelList>
                      {filteredResp.filter(r => r.id !== data.responsavelPrincipalId).length === 0 ? (
                        <div style={{ padding: '1rem', color: '#9ca3af', fontSize: '0.875rem', textAlign: 'center' }}>
                          Nenhum responsável encontrado
                        </div>
                      ) : filteredResp.filter(r => r.id !== data.responsavelPrincipalId).map(r => (
                        <ResponsavelItem
                          key={r.id}
                          $selected={false}
                          onClick={() => { set('responsavelSolidarioId', r.id); setSearch(''); }}
                        >
                          <ResponsavelNome>{r.nome}</ResponsavelNome>
                          <ResponsavelCpf>{r.cpfCnpj}{r.telefone ? ` · ${r.telefone}` : ''}</ResponsavelCpf>
                        </ResponsavelItem>
                      ))}
                    </ResponsavelList>
                  </>
                )}
                <InlineActions>
                  <LinkActionBtn type="button" onClick={() => openCreateResponsavel('solidario')}>
                    + Criar novo responsável solidário
                  </LinkActionBtn>
                </InlineActions>
              </div>

              {creatingResponsavelFor && (
                <InlineCreateCard>
                  <InlineCreateTitle>
                    {creatingResponsavelFor === 'principal'
                      ? 'Novo responsável principal'
                      : 'Novo responsável solidário'}
                  </InlineCreateTitle>

                  <InlineCreateGrid>
                    <FormGroup>
                      <Label>Nome completo <span>*</span></Label>
                      <Input
                        value={novoResponsavel.nome}
                        onChange={e => {
                          setNovoResponsavel(prev => ({ ...prev, nome: e.target.value }));
                          setNovoResponsavelErrors(prev => ({ ...prev, nome: undefined }));
                        }}
                        $error={!!novoResponsavelErrors.nome}
                        placeholder="Nome do responsável"
                      />
                      {novoResponsavelErrors.nome && <ErrorMsg>{novoResponsavelErrors.nome}</ErrorMsg>}
                    </FormGroup>

                    <FormGroup>
                      <Label>CPF/CNPJ <span>*</span></Label>
                      <Input
                        value={novoResponsavel.cpfCnpj}
                        onChange={e => {
                          setNovoResponsavel(prev => ({ ...prev, cpfCnpj: e.target.value }));
                          setNovoResponsavelErrors(prev => ({ ...prev, cpfCnpj: undefined }));
                        }}
                        $error={!!novoResponsavelErrors.cpfCnpj}
                        placeholder="000.000.000-00"
                      />
                      {novoResponsavelErrors.cpfCnpj && <ErrorMsg>{novoResponsavelErrors.cpfCnpj}</ErrorMsg>}
                    </FormGroup>

                    <FormGroup>
                      <Label>E-mail <span>*</span></Label>
                      <Input
                        type="email"
                        value={novoResponsavel.email}
                        onChange={e => {
                          setNovoResponsavel(prev => ({ ...prev, email: e.target.value }));
                          setNovoResponsavelErrors(prev => ({ ...prev, email: undefined }));
                        }}
                        $error={!!novoResponsavelErrors.email}
                        placeholder="email@exemplo.com"
                      />
                      {novoResponsavelErrors.email && <ErrorMsg>{novoResponsavelErrors.email}</ErrorMsg>}
                    </FormGroup>

                    <FormGroup>
                      <Label>Telefone <span>*</span></Label>
                      <Input
                        value={novoResponsavel.telefone}
                        onChange={e => {
                          setNovoResponsavel(prev => ({ ...prev, telefone: e.target.value }));
                          setNovoResponsavelErrors(prev => ({ ...prev, telefone: undefined }));
                        }}
                        $error={!!novoResponsavelErrors.telefone}
                        placeholder="(00) 00000-0000"
                      />
                      {novoResponsavelErrors.telefone && <ErrorMsg>{novoResponsavelErrors.telefone}</ErrorMsg>}
                    </FormGroup>

                    <FormGroup>
                      <Label>Tipo pessoa</Label>
                      <Select
                        value={novoResponsavel.tipoPessoa}
                        onChange={e => setNovoResponsavel(prev => ({ ...prev, tipoPessoa: e.target.value as '1' | '2' | '3' }))}
                      >
                        <option value="1">Pessoa Física</option>
                        <option value="2">Pessoa Jurídica</option>
                        <option value="3">Estrangeiro</option>
                      </Select>
                    </FormGroup>

                    <FormGroup style={{ gridColumn: '1 / -1' }}>
                      <Label>Endereço <span>*</span></Label>
                      <Input
                        value={novoResponsavel.endereco}
                        onChange={e => {
                          setNovoResponsavel(prev => ({ ...prev, endereco: e.target.value }));
                          setNovoResponsavelErrors(prev => ({ ...prev, endereco: undefined }));
                        }}
                        $error={!!novoResponsavelErrors.endereco}
                        placeholder="Rua, avenida, etc."
                      />
                      {novoResponsavelErrors.endereco && <ErrorMsg>{novoResponsavelErrors.endereco}</ErrorMsg>}
                    </FormGroup>

                    <FormGroup>
                      <Label>Número</Label>
                      <Input
                        value={novoResponsavel.numero}
                        onChange={e => setNovoResponsavel(prev => ({ ...prev, numero: e.target.value }))}
                        placeholder="Ex: 123"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Complemento</Label>
                      <Input
                        value={novoResponsavel.complemento}
                        onChange={e => setNovoResponsavel(prev => ({ ...prev, complemento: e.target.value }))}
                        placeholder="Apto, bloco, referência"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Bairro</Label>
                      <Input
                        value={novoResponsavel.bairro}
                        onChange={e => setNovoResponsavel(prev => ({ ...prev, bairro: e.target.value }))}
                        placeholder="Bairro"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Cidade <span>*</span></Label>
                      <Input
                        value={novoResponsavel.cidade}
                        onChange={e => {
                          setNovoResponsavel(prev => ({ ...prev, cidade: e.target.value }));
                          setNovoResponsavelErrors(prev => ({ ...prev, cidade: undefined }));
                        }}
                        $error={!!novoResponsavelErrors.cidade}
                        placeholder="Cidade"
                      />
                      {novoResponsavelErrors.cidade && <ErrorMsg>{novoResponsavelErrors.cidade}</ErrorMsg>}
                    </FormGroup>

                    <FormGroup>
                      <Label>Estado (UF) <span>*</span></Label>
                      <Input
                        value={novoResponsavel.estado}
                        onChange={e => {
                          setNovoResponsavel(prev => ({ ...prev, estado: e.target.value.toUpperCase().slice(0, 2) }));
                          setNovoResponsavelErrors(prev => ({ ...prev, estado: undefined }));
                        }}
                        $error={!!novoResponsavelErrors.estado}
                        maxLength={2}
                        placeholder="UF"
                      />
                      {novoResponsavelErrors.estado && <ErrorMsg>{novoResponsavelErrors.estado}</ErrorMsg>}
                    </FormGroup>

                    <FormGroup>
                      <Label>CEP <span>*</span></Label>
                      <Input
                        value={novoResponsavel.cep}
                        onChange={e => {
                          setNovoResponsavel(prev => ({ ...prev, cep: e.target.value }));
                          setNovoResponsavelErrors(prev => ({ ...prev, cep: undefined }));
                        }}
                        $error={!!novoResponsavelErrors.cep}
                        placeholder="00000-000"
                      />
                      {novoResponsavelErrors.cep && <ErrorMsg>{novoResponsavelErrors.cep}</ErrorMsg>}
                    </FormGroup>
                  </InlineCreateGrid>

                  <InlineCreateActions>
                    <BtnGhost type="button" onClick={closeCreateResponsavel}>
                      Cancelar
                    </BtnGhost>
                    <BtnPrimarySmall type="button" onClick={handleCreateResponsavel} disabled={savingResponsavel}>
                      {savingResponsavel ? 'Salvando...' : 'Salvar responsável'}
                    </BtnPrimarySmall>
                  </InlineCreateActions>
                </InlineCreateCard>
              )}
            </>
          )}

          {/* â”€â”€ Step 3: Contrato â”€â”€ */}
          {step === 2 && (
            <>
              <SectionTitle>Dados do Contrato</SectionTitle>
              <Grid2>
                <FullRow>
                  <CheckRow>
                    <input type="checkbox" checked={data.eRematricula} onChange={e => set('eRematricula', e.target.checked)} />
                    É rematrícula?
                  </CheckRow>
                </FullRow>
              </Grid2>

              <Grid3>
                <FormGroup>
                  <Label>Valor mensal (R$)</Label>
                  <ReadOnlyValue>
                    R$ {(parseFloat(data.valorMensal) || 0).toFixed(2).replace('.', ',')}
                  </ReadOnlyValue>
                </FormGroup>

                <FormGroup>
                  <Label>Desconto (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={data.percentualDesconto}
                    onChange={e => set('percentualDesconto', e.target.value)}
                    placeholder="0"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Valor final (R$)</Label>
                  <ReadOnlyValue>
                    R$ {valorMensalFinal.toFixed(2).replace('.', ',')}
                  </ReadOnlyValue>
                </FormGroup>
              </Grid3>

              <Grid2>
                <FormGroup>
                  <Label>Dia de vencimento</Label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={data.diaVencimento}
                    onChange={e => set('diaVencimento', e.target.value)}
                    placeholder="10"
                  />
                </FormGroup>

                <FullRow>
                  <CheckRow>
                    <input type="checkbox" checked={data.emitirNfse} onChange={e => set('emitirNfse', e.target.checked)} />
                    Emitir NFSe automaticamente
                  </CheckRow>
                </FullRow>
              </Grid2>

              <Divider />
              <DividerLabel>Matrícula</DividerLabel>

              <Grid2>
                <FormGroup>
                  <Label>Valor matrícula (R$)</Label>
                  <ReadOnlyValue>
                    R$ {valorMatriculaBase.toFixed(2).replace('.', ',')}
                  </ReadOnlyValue>
                </FormGroup>

                <FormGroup>
                  <Label>Desconto matrícula (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={data.percentualDescontoMatricula}
                    onChange={e => set('percentualDescontoMatricula', e.target.value)}
                    placeholder="0"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Valor final matrícula (R$)</Label>
                  <ReadOnlyValue>
                    R$ {valorMatriculaFinal.toFixed(2).replace('.', ',')}
                  </ReadOnlyValue>
                </FormGroup>

                <FormGroup>
                  <Label>Forma de pagamento</Label>
                  <Select value={data.formaPagamentoMatricula} onChange={e => set('formaPagamentoMatricula', e.target.value as any)}>
                    <option value="isento">Isento</option>
                    <option value="avista">À vista</option>
                    <option value="parcelado">Parcelado</option>
                  </Select>
                </FormGroup>

                {data.formaPagamentoMatricula === 'parcelado' && (
                  <FormGroup>
                    <Label>Parcelas</Label>
                    <Input type="number" min="1" max="12" value={data.parcelasMatricula} onChange={e => set('parcelasMatricula', e.target.value)} />
                  </FormGroup>
                )}
              </Grid2>

              <Divider />
              <DividerLabel>Material</DividerLabel>

              <Grid2>
                <FormGroup>
                  <Label>Valor material (R$)</Label>
                  <ReadOnlyValue>
                    R$ {valorMaterialBase.toFixed(2).replace('.', ',')}
                  </ReadOnlyValue>
                </FormGroup>

                <FormGroup>
                  <Label>Desconto material (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={data.percentualDescontoMaterial}
                    onChange={e => set('percentualDescontoMaterial', e.target.value)}
                    placeholder="0"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Valor final material (R$)</Label>
                  <ReadOnlyValue>
                    R$ {valorMaterialFinal.toFixed(2).replace('.', ',')}
                  </ReadOnlyValue>
                </FormGroup>

                <FormGroup>
                  <Label>Forma de pagamento</Label>
                  <Select value={data.formaPagamentoMaterial} onChange={e => set('formaPagamentoMaterial', e.target.value as any)}>
                    <option value="isento">Isento</option>
                    <option value="avista">À vista</option>
                    <option value="parcelado">Parcelado</option>
                  </Select>
                </FormGroup>

                {data.formaPagamentoMaterial === 'parcelado' && (
                  <FormGroup>
                    <Label>Parcelas</Label>
                    <Input type="number" min="1" max="12" value={data.parcelasMaterial} onChange={e => set('parcelasMaterial', e.target.value)} />
                  </FormGroup>
                )}
              </Grid2>
            </>
          )}

          {/* â”€â”€ Step 4: RevisÃ£o â”€â”€ */}
          {step === 3 && (
            <>
              <SectionTitle>Revise os dados antes de confirmar</SectionTitle>

              <ReviewSection>
                <ReviewHeader>Aluno</ReviewHeader>
                <ReviewGrid>
                  <ReviewItem><ReviewLabel>Nome</ReviewLabel><ReviewValue>{data.nome}</ReviewValue></ReviewItem>
                  <ReviewItem><ReviewLabel>Matrícula</ReviewLabel><ReviewValue>{data.matricula}</ReviewValue></ReviewItem>
                  <ReviewItem><ReviewLabel>Nascimento</ReviewLabel><ReviewValue>{data.dataNascimento ? new Date(data.dataNascimento + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</ReviewValue></ReviewItem>
                  <ReviewItem><ReviewLabel>Turma</ReviewLabel><ReviewValue>{turma?.nome ?? '—'}</ReviewValue></ReviewItem>
                  {data.cpf && <ReviewItem><ReviewLabel>CPF</ReviewLabel><ReviewValue>{data.cpf}</ReviewValue></ReviewItem>}
                  {data.telefone && <ReviewItem><ReviewLabel>Telefone</ReviewLabel><ReviewValue>{data.telefone}</ReviewValue></ReviewItem>}
                  {data.email && <ReviewItem style={{ gridColumn: '1 / -1' }}><ReviewLabel>E-mail</ReviewLabel><ReviewValue>{data.email}</ReviewValue></ReviewItem>}
                </ReviewGrid>
              </ReviewSection>

              <ReviewSection>
                <ReviewHeader>Responsáveis</ReviewHeader>
                <ReviewGrid>
                  <ReviewItem><ReviewLabel>Principal</ReviewLabel><ReviewValue>{principal?.nome ?? '—'}</ReviewValue></ReviewItem>
                  <ReviewItem><ReviewLabel>CPF/CNPJ</ReviewLabel><ReviewValue>{principal?.cpfCnpj ?? '—'}</ReviewValue></ReviewItem>
                  {solidario && (
                    <>
                      <ReviewItem><ReviewLabel>Solidário</ReviewLabel><ReviewValue>{solidario.nome}</ReviewValue></ReviewItem>
                      <ReviewItem><ReviewLabel>CPF/CNPJ</ReviewLabel><ReviewValue>{solidario.cpfCnpj}</ReviewValue></ReviewItem>
                    </>
                  )}
                </ReviewGrid>
              </ReviewSection>

              <ReviewSection>
                <ReviewHeader>Contrato</ReviewHeader>
                <ReviewGrid>
                  <ReviewItem><ReviewLabel>Valor mensal</ReviewLabel><ReviewValue>{fmt(data.valorMensal)}</ReviewValue></ReviewItem>
                  <ReviewItem><ReviewLabel>Desconto</ReviewLabel><ReviewValue>{data.percentualDesconto || '0'}%</ReviewValue></ReviewItem>
                  <ReviewItem>
                    <ReviewLabel>Valor final</ReviewLabel>
                    <ReviewValue>{fmt(valorMensalFinal)}</ReviewValue>
                  </ReviewItem>
                  <ReviewItem><ReviewLabel>Vencimento</ReviewLabel><ReviewValue>Dia {data.diaVencimento}</ReviewValue></ReviewItem>
                  <ReviewItem><ReviewLabel>Matrícula</ReviewLabel><ReviewValue>{fmt(valorMatriculaFinal)} ({data.percentualDescontoMatricula || '0'}% desc.) · {fmtPag(data.formaPagamentoMatricula)}</ReviewValue></ReviewItem>
                  <ReviewItem><ReviewLabel>Material</ReviewLabel><ReviewValue>{fmt(valorMaterialFinal)} ({data.percentualDescontoMaterial || '0'}% desc.) · {fmtPag(data.formaPagamentoMaterial)}</ReviewValue></ReviewItem>
                  <ReviewItem><ReviewLabel>NFSe</ReviewLabel><ReviewValue>{data.emitirNfse ? 'Sim' : 'Não'}</ReviewValue></ReviewItem>
                  <ReviewItem><ReviewLabel>Rematrícula</ReviewLabel><ReviewValue>{data.eRematricula ? 'Sim' : 'Não'}</ReviewValue></ReviewItem>
                </ReviewGrid>
              </ReviewSection>
            </>
          )}
        </Body>

        {/* Footer */}
        <Footer>
          <BtnCancel onClick={onClose}>Cancelar</BtnCancel>
          <NavGroup>
            {step > 0 && <BtnBack onClick={back}>← Anterior</BtnBack>}
            {step < 3
              ? <BtnNext onClick={next}>Próximo →</BtnNext>
              : (
                <BtnSubmit onClick={handleSubmit} disabled={submitting}>
                  <CheckCircle size={16} />
                  {submitting ? 'Salvando...' : 'Confirmar Matrícula'}
                </BtnSubmit>
              )}
          </NavGroup>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default NovoAlunoWizard;

